import { Controller, Control, FieldValues, FieldPath } from "react-hook-form";
import { useEffect, useState, useRef } from "react";
import { Autocomplete, Box } from "@mui/material";
import ExpandMoreOutlinedIcon from "@mui/icons-material/ExpandMoreOutlined";
import InputTextSearchSelectFetch from "./customTextInput";
import "./SearchSelect.css";

type PropsSearch<ObjectType, FormValues extends FieldValues> = {
  name: FieldPath<FormValues>;
  label: string;
  control: Control<FormValues>;
  disabled?: boolean;
  inputStyles?: string;
  fetchOptions: (...params: any[]) => Promise<ObjectType[]>;
  propertyLabel: keyof ObjectType;
  propertyLabelDNI?: keyof ObjectType;
  onClear?: () => void;
  required?: boolean;
  onSelected?: (data: ObjectType) => void;
};

export default function SearchSelectFetch<
  ObjectType extends { id?: string; value?: string | number },
  FormValues extends FieldValues,
>({
  control,
  name,
  label,
  disabled,
  fetchOptions,
  inputStyles,
  propertyLabel,
  propertyLabelDNI,
  onClear,
  required,
  onSelected,
}: PropsSearch<ObjectType, FormValues>) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ObjectType[]>([]);
  const [loading, setLoading] = useState<boolean>(open);

  const fetchOptionsRef = useRef(fetchOptions);
  fetchOptionsRef.current = fetchOptions;

  const getOptionLabel = (option: ObjectType): string => {
    const label = String(option[propertyLabel] ?? "");
    const dni = propertyLabelDNI ? option[propertyLabelDNI] : undefined;
    return dni ? `${label} - (${dni})` : label;
  };

  useEffect(() => {
    if (open) {
      fetchOptionsRef
        .current()
        .then((optionsFromDb) => {
          setOptions([...optionsFromDb]);
        })
        .catch((e) => {
          setOptions([]);
          console.log(e);
        })
        .finally(() => {
          setLoading(false);
        }); }
  }, [open]);

  return (
    <Controller
      rules={{ required }}
      name={name}
      control={control}
      render={({
        field: { value, onChange, ...restField },
        fieldState: { error },
      }) => (
        <Autocomplete
          disabled={disabled}
          clearOnEscape
          className="SearchAutocomplete"
          popupIcon={<ExpandMoreOutlinedIcon />}
          options={options}
          isOptionEqualToValue={(option, value) =>
            (option.id != null && option.id === value.id) ||
            (option.value != null && option.value === value.value)
          }
          getOptionLabel={getOptionLabel}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              {getOptionLabel(option)}
            </Box>
          )}
          loading={loading}
          value={value || null}
          renderInput={(params) => (
            <InputTextSearchSelectFetch
              params={params}
              isOpen={open}
              label={label}
              error={error}
              loading={loading}
              styles={inputStyles}
              disabled={disabled}
            />
          )}
          onChange={(e, data, reason) => {
            if (onClear && reason === "clear") onClear();
            if (onSelected && reason === "selectOption" && data != null) onSelected(data);
            return onChange(data);
          }}
          open={open}
          onOpen={() => {
            setOpen(true);
            setLoading(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          {...restField}
        />
      )}
    />
  );
}
